import {
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { ReferralSummary } from "../../../../types/referral";

interface ReferralHistoryTableProps {
  referrals: ReferralSummary[];
}

type ChipColor =
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning";

const statusChipColor = (status: string): ChipColor => {
  switch (status.toLowerCase()) {
    case "pending":
      return "warning";
    case "completed":
      return "success";
    case "expired":
      return "default";
    case "cancelled":
      return "error";
    default:
      return "default";
  }
};

const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
};

const ReferralHistoryTable: React.FC<ReferralHistoryTableProps> = ({
  referrals,
}) => {
  const { t } = useTranslation();

  return (
    <Card elevation={2} sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {t("profile.referral.referralHistory")}
        </Typography>

        {referrals.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: "center",
              color: "#9ca3af",
            }}
          >
            <Typography variant="body1">
              {t("profile.referral.noReferralsYet")}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                    {t("profile.referral.referredUser")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                    {t("profile.referral.status")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                    {t("profile.referral.dateReferred")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#374151" }}>
                    {t("profile.referral.reward")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referrals.map((referral) => (
                  <TableRow
                    key={referral.id}
                    sx={{ "&:last-child td": { border: 0 } }}
                  >
                    <TableCell
                      className="notranslate"
                      translate="no"
                      sx={{ color: "#374151" }}
                    >
                      {referral.refereeName}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={referral.status}
                        color={statusChipColor(referral.status)}
                        size="small"
                        sx={{ fontWeight: 500, textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#6b7280" }}>
                      {formatDate(referral.createdAt)}
                    </TableCell>
                    <TableCell>
                      {referral.rewardUsed ? (
                        <Chip
                          label={t("profile.referral.rewardUsed")}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      ) : referral.status.toLowerCase() === "completed" ? (
                        <Chip
                          label={t("profile.referral.rewardEarned")}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralHistoryTable;
